import { Alert } from '@akin-home/alerts';
import {
  Alert as AlertGraphql,
  CalendarItemType,
  CreateAlertInput,
  UpdateAlertInput as UpdateAlertGraphqlInput,
} from '@akin-home/amplify';
import { createReminderItem } from '@akin-home/calendar-reminders';
import { createChecklistItem } from '@akin-home/checklists';
import {
  computeUntilDate,
  createRRule,
  getUntilDateString,
  serializeRRule,
} from '@akin-home/recurrence';
import { JSONStringifySafe } from '@akin-home/utils/json';
import { UpdateAlertInput } from '../types';

export const alertGraphqlToModel = (alert: AlertGraphql): Alert => {
  return {
    type: CalendarItemType.Alert,
    id: alert.id || '',
    title: alert.title || '',
    emoji: alert.emoji || undefined,
    habitatID: alert.habitatID || '',
    start: alert.start ? new Date(alert.start) : new Date(),
    checklist:
      Array.isArray(alert.checklist) || typeof alert.checklist === 'string'
        ? JSON.parse(alert.checklist).map(createChecklistItem)
        : [],
    participants: [],
    participant_ids: (alert.participantIDS || []) as string[],
    recurrence: alert.recurrence && createRRule(alert.recurrence),
    recurrenceUntil: alert.recurrenceUntil
      ? new Date(alert.recurrenceUntil)
      : undefined,
    imageUrl: alert.imageURL || undefined,
    image: alert.image || undefined,
    imageCache: alert.image || undefined,
    goalID: alert.goalID || undefined,
    color: alert.color || undefined,
    reminders:
      Array.isArray(alert.reminders) || typeof alert.reminders === 'string'
        ? JSON.parse(alert.reminders).map(createReminderItem)
        : [],
  };
};

export const alertModelToCreateGraphql = (alert: Alert): CreateAlertInput => ({
  habitatID: alert.habitatID,
  emoji: alert.emoji,
  title: alert.title,
  type: CalendarItemType.Alert,
  start: alert.start.toISOString(),
  recurrence: alert.recurrence && serializeRRule(alert.recurrence),
  recurrenceUntil: alert.recurrence
    ? computeUntilDate(alert.recurrence).toISOString()
    : null,
  participantIDS: alert.participant_ids,
  checklist: JSONStringifySafe(alert.checklist, null),
  notes: alert.notes,
  imageID: alert.image?.id,
  imageURL: alert.imageUrl,
  goalID: alert.goalID,
  color: alert.color || undefined,
  reminders: JSONStringifySafe(alert.reminders, null),
});

export const alertModelToUpdateGraphql = (
  alert: Alert
): UpdateAlertGraphqlInput => ({
  ...alertModelToCreateGraphql(alert),
  id: alert.id,
});

export const alertModelToUpdateData = (alert: Alert): UpdateAlertInput => ({
  ...alert,
  start: alert.start.toISOString(),
  recurrence: alert.recurrence && serializeRRule(alert.recurrence),
  recurrenceUntil: getUntilDateString(alert.recurrence) || null,
});

export const alertDataToModel = (alert: any): Alert => ({
  ...alert,
  start: new Date(alert.start),
});
